import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";

// Top-level tabs used in Diagnostics
export type DiagnosticsTab = "vitals" | "cognition" | "recovery" | "labs" | "social";

type DiagnosticsContextValue = {
  // Panel state
  isOpen: boolean;
  // Optional �source�/provider (e.g., which module opened Diagnostics)
  provider: string | null;

  // Tab state
  /** One-time tab hint when opening the panel (does not overwrite the user's saved preference). */
  initialTab?: DiagnosticsTab;
  /** User�s remembered tab across sessions. */
  preferredTab: DiagnosticsTab;

  // Actions
  open: (opts?: { provider?: string | null; tab?: DiagnosticsTab }) => void;
  close: () => void;
  toggle: (opts?: { provider?: string | null; tab?: DiagnosticsTab }) => void;

  /** Update the user's remembered tab (persists to localStorage). */
  setPreferredTab: (tab: DiagnosticsTab) => void;
};

const DiagnosticsContext = createContext<DiagnosticsContextValue | undefined>(undefined);

// LocalStorage key for last-opened tab
const LAST_TAB_KEY = "diag:lastTab";
const DEFAULT_TAB: DiagnosticsTab = "vitals";

// Safe localStorage getters/setters
function safeGetLastTab(): DiagnosticsTab {
  try {
    const raw = localStorage.getItem(LAST_TAB_KEY) as DiagnosticsTab | null;
    return raw === "vitals" || raw === "cognition" || raw === "recovery" || raw === "labs" || raw === "social"
      ? raw
      : DEFAULT_TAB;
  } catch {
    return DEFAULT_TAB;
  }
}

function safeSetLastTab(tab: DiagnosticsTab) {
  try {
    localStorage.setItem(LAST_TAB_KEY, tab);
  } catch {
    // ignore (private mode / SSR)
  }
}

export function DiagnosticsProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [provider, setProvider] = useState<string | null>(null);

  // Persisted user preference for which tab they use most recently
  const [preferredTab, setPreferredTabState] = useState<DiagnosticsTab>(() => safeGetLastTab());
  const [initialTab, setInitialTab] = useState<DiagnosticsTab | undefined>(undefined);

  // Public setter that persists preference
  const setPreferredTab = useCallback((tab: DiagnosticsTab) => {
    setPreferredTabState(tab);
    safeSetLastTab(tab);
  }, []);

  // OPEN: optional provider + optional tab hint
  const open = useCallback((opts?: { provider?: string | null; tab?: DiagnosticsTab }) => {
    if (opts && "provider" in opts) setProvider(opts.provider ?? null);
    if (opts && "tab" in opts) setInitialTab(opts.tab);
    setIsOpen(true);
  }, []);

  // CLOSE: clear only panel state; keep provider for telemetry if desired
  const close = useCallback(() => {
    setIsOpen(false);
    // Do not clear preferredTab; it's the saved memory of last use
    // Clear initialTab to avoid �sticky� forced tab on next open
    setInitialTab(undefined);
  }, []);

  // TOGGLE behaves like open/close but accepts the same options
  const toggle = useCallback((opts?: { provider?: string | null; tab?: DiagnosticsTab }) => {
    if (!isOpen) {
      open(opts);
    } else {
      close();
    }
  }, [isOpen, open, close]);

  // Keep preferredTab in sync if localStorage changes from another tab/window (rare)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === LAST_TAB_KEY && e.newValue) {
        const next = e.newValue as DiagnosticsTab;
        if (next && next !== preferredTab) {
          setPreferredTabState(
            next === "vitals" || next === "cognition" || next === "recovery" || next === "labs" || next === "social"
              ? next
              : DEFAULT_TAB
          );
        }
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [preferredTab]);

  const value = useMemo<DiagnosticsContextValue>(
    () => ({
      isOpen,
      provider,
      initialTab,
      preferredTab,
      open,
      close,
      toggle,
      setPreferredTab,
    }),
    [isOpen, provider, initialTab, preferredTab, open, close, toggle, setPreferredTab]
  );

  return <DiagnosticsContext.Provider value={value}>{children}</DiagnosticsContext.Provider>;
}

export function useDiagnostics() {
  const ctx = useContext(DiagnosticsContext);
  if (!ctx) throw new Error("useDiagnostics must be used inside DiagnosticsProvider");
  return ctx;
}
